import { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch, isSignedIn } from '../api';

const SUGGESTIONS = [
  'Generate an image of a cyberpunk city at sunset',
  'Generate a video of ocean waves crashing on rocks',
  'Scrape https://news.ycombinator.com into markdown',
  'Run Python: print(sum(range(1, 101)))',
  'Create a QR code for https://invoke-api.com',
  'Translate "Hello world" to Japanese, French, and Bulgarian',
  'Generate an image, then edit it to add a dragon',
  'Say "Welcome to invokeAPI" in a deep voice',
];

export default function Playground({ navigate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [gallery, setGallery] = useState([]); // collected visual outputs
  const [previewOpen, setPreviewOpen] = useState(true);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Extract media from a tool result and add to gallery
  const addToGallery = useCallback((msg) => {
    if (!msg.result) return;
    const item = { build: msg.build, explanation: msg.explanation, ts: msg.ts, params: msg.params };

    if (msg.result._binary) {
      const ct = msg.result.contentType || '';
      const src = `data:${ct};base64,${msg.result.data}`;
      if (ct.startsWith('image/')) {
        setGallery(g => [...g, { ...item, type: 'image', src }]);
      } else if (ct.startsWith('audio/')) {
        setGallery(g => [...g, { ...item, type: 'audio', src }]);
      } else if (ct.startsWith('video/')) {
        setGallery(g => [...g, { ...item, type: 'video', src }]);
      }
    } else if (msg.result.image_url || msg.result.url) {
      const url = msg.result.image_url || msg.result.url;
      if (typeof url === 'string' && (url.endsWith('.png') || url.endsWith('.jpg') || url.endsWith('.webp') || url.includes('image') || msg.build?.includes('flux') || msg.build?.includes('qr'))) {
        setGallery(g => [...g, { ...item, type: 'image', src: url }]);
      }
    } else if (msg.result.images && Array.isArray(msg.result.images)) {
      msg.result.images.forEach((img, idx) => {
        const src = img.url || img.image_url || (typeof img === 'string' ? img : null);
        if (src) setGallery(g => [...g, { ...item, type: 'image', src, note: `#${idx + 1}` }]);
      });
    }
  }, []);

  async function send(text) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    const newHistory = [...history, { role: 'user', content: msg }];
    setHistory(newHistory);
    setLoading(true);

    try {
      const res = await authFetch('/v1/playground/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history: newHistory.slice(-20) }),
      });
      const data = await res.json();

      if (data.action === 'call') {
        const toolMsg = {
          role: 'assistant', type: 'tool_call',
          build: data.build, explanation: data.explanation,
          params: data.params, result: data.result,
          status: data.status, charged: data.charged,
          balance: data.balance, ts: Date.now(),
        };
        setMessages(prev => [...prev, toolMsg]);
        addToGallery(toolMsg);

        const resultSummary = data.result?._binary
          ? `[Binary ${data.result.contentType}, ${(data.result.size / 1024).toFixed(1)}KB]`
          : JSON.stringify(data.result).slice(0, 500);
        setHistory(prev => [...prev, {
          role: 'assistant',
          content: `I called ${data.build} with ${JSON.stringify(data.params)}. Result: ${resultSummary}`,
        }]);
      } else {
        const textMsg = { role: 'assistant', content: data.content || data.error || 'No response', ts: Date.now() };
        setMessages(prev => [...prev, textMsg]);
        setHistory(prev => [...prev, { role: 'assistant', content: data.content || '' }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${e.message}`, ts: Date.now() }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  const hasGallery = gallery.length > 0;

  // ─── Styles ──────────────────────────────────────────────────────────
  const s = {
    outer: { display: 'flex', height: 'calc(100vh - 140px)', gap: 0 },
    chatCol: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
    header: { textAlign: 'center', padding: '12px 0 8px' },
    h1: { fontSize: 22, fontWeight: 700, margin: 0 },
    sub: { color: 'var(--muted)', fontSize: 13, margin: '4px 0 0' },
    chat: { flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 12 },
    userBubble: { alignSelf: 'flex-end', background: 'linear-gradient(135deg, var(--accent), #6558d4)', color: '#fff', borderRadius: '16px 16px 4px 16px', padding: '10px 16px', maxWidth: '80%', fontSize: 14, lineHeight: 1.5 },
    aiBubble: { alignSelf: 'flex-start', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '16px 16px 16px 4px', padding: '12px 16px', maxWidth: '85%', fontSize: 14, lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap' },
    toolCard: { alignSelf: 'flex-start', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, maxWidth: '90%', overflow: 'hidden' },
    toolHeader: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'rgba(124,111,239,0.06)' },
    toolBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: 'rgba(124,111,239,0.15)', color: 'var(--accent)' },
    statusBadge: (ok) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: ok ? 'var(--green)' : 'var(--red)' }),
    toolBody: { padding: '12px 14px' },
    explanation: { fontSize: 13, color: 'var(--text)', marginBottom: 10, lineHeight: 1.5 },
    resultBox: { background: 'rgba(0,0,0,0.15)', borderRadius: 8, padding: '10px 12px', maxHeight: 250, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace', lineHeight: 1.5, color: 'var(--muted)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    toolMeta: { display: 'flex', gap: 12, padding: '8px 14px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--muted)' },
    inputArea: { display: 'flex', gap: 10, padding: '12px 0', borderTop: '1px solid var(--border)' },
    input: { flex: 1, padding: '12px 16px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4, minHeight: 44, maxHeight: 120 },
    sendBtn: { padding: '0 20px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, var(--accent), #6558d4)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end', height: 44 },
    sendBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    suggestions: { display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', padding: '24px 0' },
    chip: { padding: '8px 14px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', transition: 'all 0.15s', maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    btn: { padding: '10px 24px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'linear-gradient(135deg, var(--accent), #6558d4)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
    empty: { textAlign: 'center', padding: '40px 24px', color: 'var(--muted)' },
    clearBtn: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', padding: '4px 8px' },
    // Preview sidebar
    previewCol: { width: 320, borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--bg)', flexShrink: 0 },
    previewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' },
    previewTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
    previewCount: { fontSize: 11, color: 'var(--muted)', background: 'var(--bg2)', padding: '2px 8px', borderRadius: 99 },
    previewGrid: { flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
    previewItem: (selected) => ({
      borderRadius: 10, border: selected ? '2px solid var(--accent)' : '1px solid var(--border)',
      overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s',
      background: 'var(--bg2)', boxShadow: selected ? '0 0 12px rgba(124,111,239,0.3)' : 'none',
    }),
    previewImg: { width: '100%', display: 'block', borderRadius: '9px 9px 0 0' },
    previewLabel: { padding: '6px 10px', fontSize: 11, color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    previewToggle: { position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRight: 'none', borderRadius: '8px 0 0 8px', padding: '12px 6px', cursor: 'pointer', color: 'var(--muted)', fontSize: 14, lineHeight: 1 },
    // Lightbox
    lightbox: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
    lightboxInner: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, overflow: 'hidden' },
    lightboxClose: { position: 'absolute', top: 20, right: 20, color: '#fff', fontSize: 28, cursor: 'pointer', background: 'rgba(0,0,0,0.5)', borderRadius: 99, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' },
  };

  const mobileCSS = `@media (max-width: 900px) { .pg-preview { display: none !important; } .pg-toggle { display: none !important; } }`;

  if (!isSignedIn()) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 24px' }}>
        <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>AI Playground</h2>
        <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24 }}>
          Chat with an AI that can call 40+ API tools for you — images, video, audio, code, web scraping, and more.
        </p>
        <button style={s.btn} onClick={() => navigate('signin')}>Sign in</button>
        <span style={{ margin: '0 12px', color: 'var(--muted)' }}>or</span>
        <button style={{ ...s.btn, background: 'var(--bg3)', color: 'var(--text)', boxShadow: 'none' }} onClick={() => navigate('getkey')}>Create account</button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <style>{mobileCSS}</style>

      {/* Lightbox for full-size preview */}
      {selectedPreview && (
        <div style={s.lightbox} onClick={() => setSelectedPreview(null)}>
          <button style={s.lightboxClose} onClick={() => setSelectedPreview(null)}>&#x2715;</button>
          <div style={s.lightboxInner} onClick={e => e.stopPropagation()}>
            {selectedPreview.type === 'image' && (
              <img src={selectedPreview.src} alt="preview" style={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }} />
            )}
            {selectedPreview.type === 'video' && (
              <video controls autoPlay src={selectedPreview.src} style={{ maxWidth: '90vw', maxHeight: '90vh' }} />
            )}
            {selectedPreview.type === 'audio' && (
              <div style={{ background: 'var(--bg2)', padding: 40, borderRadius: 12 }}>
                <audio controls autoPlay src={selectedPreview.src} style={{ width: 400 }} />
              </div>
            )}
          </div>
        </div>
      )}

      <div style={s.outer}>
        {/* Chat column */}
        <div style={s.chatCol}>
          <div style={s.header}>
            <h1 style={s.h1}>AI Playground</h1>
            <p style={s.sub}>
              Tell me what you need — I'll pick the right API and call it for you.
              {messages.length > 0 && <button style={s.clearBtn} onClick={() => { setMessages([]); setHistory([]); setGallery([]); }}>Clear chat</button>}
            </p>
          </div>

          <div style={s.chat}>
            {messages.length === 0 && (
              <div style={s.empty}>
                <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.2 }}>&#x1F916;</div>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, color: 'var(--text)' }}>What would you like to do?</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>I can generate images, videos, audio, run code, scrape websites, translate, and more. Results appear in the preview panel.</div>
                <div style={s.suggestions}>
                  {SUGGESTIONS.map((sug, i) => (
                    <div key={i} style={s.chip} onClick={() => send(sug)}
                      onMouseOver={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.color = 'var(--text)'; }}
                      onMouseOut={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.color = 'var(--muted)'; }}>
                      {sug}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => {
              if (msg.role === 'user') {
                return <div key={i} style={s.userBubble}>{msg.content}</div>;
              }
              if (msg.type === 'tool_call') {
                const ok = msg.status >= 200 && msg.status < 400;
                const isBinary = msg.result?._binary;
                const isImage = isBinary && msg.result.contentType?.startsWith('image/');
                const isAudio = isBinary && msg.result.contentType?.startsWith('audio/');
                const isVideo = isBinary && msg.result.contentType?.startsWith('video/');
                const isMedia = isImage || isAudio || isVideo;

                return (
                  <div key={i} style={s.toolCard}>
                    <div style={s.toolHeader}>
                      <span style={s.toolBadge}>{msg.build}</span>
                      <span style={s.statusBadge(ok)}>{ok ? 'Success' : `Error ${msg.status}`}</span>
                      {msg.charged > 0 && <span style={{ fontSize: 11, color: 'var(--accent2)' }}>-${msg.charged.toFixed(3)}</span>}
                    </div>
                    <div style={s.toolBody}>
                      {msg.explanation && <div style={s.explanation}>{msg.explanation}</div>}
                      {isMedia ? (
                        <div style={{ borderRadius: 8, overflow: 'hidden', background: 'rgba(0,0,0,0.15)' }}>
                          {isImage && (
                            <img src={`data:${msg.result.contentType};base64,${msg.result.data}`} alt="result"
                              style={{ maxWidth: '100%', maxHeight: 250, display: 'block', cursor: 'pointer' }}
                              onClick={() => setSelectedPreview({ type: 'image', src: `data:${msg.result.contentType};base64,${msg.result.data}` })} />
                          )}
                          {isAudio && <audio controls src={`data:${msg.result.contentType};base64,${msg.result.data}`} style={{ width: '100%', padding: 12 }} />}
                          {isVideo && <video controls src={`data:${msg.result.contentType};base64,${msg.result.data}`} style={{ maxWidth: '100%', maxHeight: 250 }} />}
                        </div>
                      ) : (
                        <div style={s.resultBox}>
                          <pre style={{ margin: 0 }}>{typeof msg.result === 'string' ? msg.result : JSON.stringify(msg.result, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                    <div style={s.toolMeta}>
                      <span>Params: {JSON.stringify(msg.params).slice(0, 80)}{JSON.stringify(msg.params).length > 80 ? '...' : ''}</span>
                      {msg.balance != null && <span style={{ marginLeft: 'auto' }}>Balance: ${msg.balance.toFixed(2)}</span>}
                    </div>
                  </div>
                );
              }
              return <div key={i} style={s.aiBubble}>{msg.content}</div>;
            })}

            {loading && (
              <div style={{ ...s.aiBubble, color: 'var(--muted)' }}>
                <span className="thinking-dots">Thinking</span>
                <style>{`.thinking-dots::after { content: ''; animation: dots 1.5s steps(4,end) infinite; } @keyframes dots { 0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; } }`}</style>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div style={s.inputArea}>
            <textarea ref={inputRef} style={s.input} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey} placeholder="Ask me to generate images, scrape websites, run code, translate text..." rows={1} />
            <button style={{ ...s.sendBtn, ...(loading || !input.trim() ? s.sendBtnDisabled : {}) }}
              onClick={() => send()} disabled={loading || !input.trim()}>Send</button>
          </div>
        </div>

        {/* Preview sidebar toggle */}
        {!previewOpen && hasGallery && (
          <div className="pg-toggle" style={s.previewToggle} onClick={() => setPreviewOpen(true)} title="Show preview">
            &#x1F5BC; {gallery.length}
          </div>
        )}

        {/* Preview sidebar */}
        {previewOpen && hasGallery && (
          <div className="pg-preview" style={s.previewCol}>
            <div style={s.previewHeader}>
              <span style={s.previewTitle}>Preview</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={s.previewCount}>{gallery.length} item{gallery.length !== 1 ? 's' : ''}</span>
                <button onClick={() => setPreviewOpen(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: 0 }}>&#x2715;</button>
              </div>
            </div>
            <div style={s.previewGrid}>
              {[...gallery].reverse().map((item, i) => (
                <div key={i} style={s.previewItem(false)} onClick={() => setSelectedPreview(item)}>
                  {item.type === 'image' && (
                    <img src={item.src} alt={item.explanation || 'generated'} style={s.previewImg}
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  {item.type === 'video' && (
                    <video src={item.src} style={{ ...s.previewImg, maxHeight: 180 }} muted />
                  )}
                  {item.type === 'audio' && (
                    <div style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, marginBottom: 6 }}>&#x1F3B5;</div>
                      <audio controls src={item.src} style={{ width: '100%' }} />
                    </div>
                  )}
                  <div style={s.previewLabel}>
                    <span style={{ fontWeight: 500, color: 'var(--text)' }}>{item.build}{item.note ? ` ${item.note}` : ''}</span>
                    <span>{new Date(item.ts).toLocaleTimeString()}</span>
                  </div>
                  {item.explanation && (
                    <div style={{ padding: '0 10px 8px', fontSize: 11, color: 'var(--muted)', lineHeight: 1.4 }}>
                      {item.explanation.slice(0, 80)}{item.explanation.length > 80 ? '...' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
